import { chunkArray } from '@iotize/common/array';
import { bufferToHexString } from '@iotize/common/byte-converter';
import { DecoderFct, EncoderFct } from '@iotize/common/converter/api';
import { ProgressState } from '@iotize/common/progress/api';
import {
  BodyDecoder,
  BodyEncoder,
  TapClientInterface,
  TapRequestFrame,
} from '@iotize/tap/client/api';
import { TapRequestFrameBuilder } from '@iotize/tap/client/impl';
import { Observable, defer } from 'rxjs';
import { first } from 'rxjs/operators';

import { ServiceCallType } from './definitions';
import { PathParameter } from './path-parameter';
import { TapResponse } from './response-impl';
import { TapError } from './tap-errors';

export namespace ServiceCallRunner {
  export interface AppendProgress extends ProgressState {
    /**
     * Currently sent byte offset
     */
    byteOffset: number;
  }
}

export function serviceCallToString(
  call: ServiceCallType<any, any>,
  options?: {
    skipBody?: boolean;
  },
) {
  function printObject(obj: any): string {
    if (obj instanceof Uint8Array) {
      return '0x' + bufferToHexString(obj);
    } else if (typeof obj === 'object') {
      return (
        '{' +
        Object.keys(obj)
          .map((key) => `"${key}":${printObject(obj[key])}`)
          .join(',') +
        '}'
      );
    } else {
      return JSON.stringify(obj);
    }
  }
  const lwm2mPath = ServiceCallRunner.resolvePathParameters(call);
  const pathAlias = call.pathAlias
    ? PathParameter.fillAllParams(call.pathAlias, call.pathParameters || {})
    : undefined;
  let result = `${TapRequestFrame.MethodType[call.method]} ${
    pathAlias || lwm2mPath
  }`;
  if (call.pathAlias) {
    result += ` (${lwm2mPath})`;
  }
  if (!options?.skipBody && call.body !== undefined) {
    result += ' ' + printObject(call.body);
  }
  return result;
}

export class ServiceCallRunner {
  constructor(
    public client: TapClientInterface,
    public options: {
      chunkSize: number; // chunk size for append requests
    } = {
      chunkSize: 220,
    },
  ) {}

  get(path: string, body?: Uint8Array) {
    return this.execute<Uint8Array, Uint8Array>({
      method: TapRequestFrame.MethodType.GET,
      path,
      body,
    });
  }

  put(path: string, body?: Uint8Array) {
    return this.execute<Uint8Array, Uint8Array>({
      method: TapRequestFrame.MethodType.PUT,
      path,
      body,
    });
  }

  post(path: string, body?: Uint8Array) {
    return this.execute<Uint8Array, Uint8Array>({
      method: TapRequestFrame.MethodType.POST,
      path,
      body,
    });
  }

  prepare<BodyType, ReturnType>(
    call: ServiceCallType<BodyType, ReturnType>,
  ): Observable<TapResponse<ReturnType>> {
    return defer(async () => {
      const tapRequestFrame = ServiceCallRunner.toTapRequest(call);

      // const debugArgs: string[] = [`CALL: ${call.method} ${call.path} body=`];
      // debugArgs.push(
      //     call.body instanceof Uint8Array
      //         ? bufferToHexString(call.body)
      //         : call.body
      // );
      // debugArgs.push(`(${args[0].toString()})`);
      try {
        const tapResponseFrame = await this.client
          .request(tapRequestFrame)
          .toPromise();
        if (!tapResponseFrame) {
          throw new Error(`No Tap response`);
        }

        const response = new TapResponse<ReturnType>(
          tapResponseFrame,
          tapRequestFrame,
        );

        const responseBodyDecoder =
          ServiceCallRunner.resolveResponseBodyDecoder(call);
        if (responseBodyDecoder) {
          response.setBodyDecoder({
            decode: responseBodyDecoder,
          });
        }

        if (!response.isSuccessful()) {
          response.setError(TapError.reponseStatusError(response, call));
        }
        return response;
      } catch (err) {
        if (!(err instanceof TapError)) {
          throw TapError.executeRequestError(call, err as Error);
        }
        throw err;
      }
    });
    // return response$.pipe(
    //     tap(
    //         response => {
    //             debug(this.constructor.name, ...debugArgs, '=>', response.toString());
    //         },
    //         err => {
    //             debug(this.constructor.name, ...debugArgs, ` => ${err.message}`);
    //         }
    //     )
    // );
  }

  public execute<BodyType, ReturnType>(
    call: ServiceCallType<BodyType, ReturnType>,
  ): Promise<TapResponse<ReturnType>> {
    return this.prepare(call).pipe(first()).toPromise() as Promise<
      TapResponse<ReturnType>
    >;
  }

  public createAppendCalls(
    call: ServiceCallType<Uint8Array>,
    chunkSize: number = this.options.chunkSize,
  ): ServiceCallType<Uint8Array, void>[] {
    const chunks = chunkArray(
      Array.from(call.body || new Uint8Array()),
      chunkSize,
    ).map((chunk, index) => {
      return {
        ...call,
        body: Uint8Array.from(chunk),
        // Uint8Array.from([
        //     index,
        //     ...chunk
        // ])
      };
    });
    return chunks;
  }

  public prepareAppendCall(
    call: ServiceCallType<Uint8Array>,
    chunkSize: number = this.options.chunkSize,
  ): Observable<ServiceCallRunner.AppendProgress> {
    return new Observable((emitter) => {
      let isCancelled = false;
      (async () => {
        try {
          const calls = this.createAppendCalls(call, chunkSize);
          let index = 0;
          let byteOffset = 0;
          for (const call of calls) {
            if (isCancelled) {
              return;
            }
            const chunk = call.body as Uint8Array;
            const progress: ServiceCallRunner.AppendProgress = {
              total: chunk.length,
              loaded: index + 1,
              byteOffset,
            };
            emitter.next(progress);
            const response = await this.execute({
              method: call.method || TapRequestFrame.MethodType.PUT,
              path: call.path,
              body: chunk,
            });
            try {
              response.successful();
            } catch (err) {
              throw TapError.appendChunkError(err as Error, chunk, progress);
            }
            byteOffset += chunk.length;
            index++;
          }
          emitter.complete();
        } catch (err) {
          emitter.error(err);
          return;
        }
      })();

      return () => {
        // TODO throw error on cancel
        isCancelled = true;
      };
    });
  }

  public static toTapRequest<BodyType, ReturnType>(
    call: ServiceCallType<BodyType, ReturnType>,
  ): TapRequestFrame {
    const path = ServiceCallRunner.resolvePathParameters(call);
    const body = ServiceCallRunner.encodeBody(call);
    return TapRequestFrameBuilder.create(call.method, path, body);
  }

  public static resolveResponseBodyDecoder<BodyType, ReturnType>(
    call: ServiceCallType<BodyType, ReturnType>,
  ) {
    if (call.responseBodyDecoder) {
      return toDecoderFunction(call.responseBodyDecoder);
    }
    return undefined;
  }

  public static resolvePathParameters(info: {
    path: string;
    pathParameters?: Record<string, string | number>;
  }) {
    return PathParameter.fillAllParams(info.path, info.pathParameters || {});
  }

  /**
   * Encode body according to options and configuration
   */
  public static encodeBody(options: ServiceCallType): Uint8Array | undefined {
    if (options.body === undefined) {
      return undefined;
    }
    const encoded = options.bodyEncoder
      ? toEncoderFunction(options.bodyEncoder)(options.body)
      : options.body;
    if (encoded !== undefined && !(encoded instanceof Uint8Array)) {
      throw TapError.invalidServiceCallConfiguration(
        `Request body encoder is required for call to ${serviceCallToString(
          options,
        )}`,
      );
    }
    return encoded;
  }
}

function toEncoderFunction<T>(
  classOrFunction: BodyEncoder<T> | EncoderFct<T, Uint8Array>,
): EncoderFct<T, Uint8Array> {
  return typeof classOrFunction === 'function'
    ? classOrFunction
    : classOrFunction.encode.bind(classOrFunction);
}

function toDecoderFunction<T>(
  classOrFunction: BodyDecoder<T> | DecoderFct<Uint8Array, T>,
): DecoderFct<Uint8Array, T> {
  return typeof classOrFunction === 'function'
    ? classOrFunction
    : classOrFunction.decode.bind(classOrFunction);
}
